import React, { useContext, useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import './pdfStyles.css';
import { filter, find, map } from 'lodash';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { titleize } from 'inflection';
import { useQuery } from '@apollo/client/react';
import { Activity, ManagersType, NoteSectionType, Settings } from '../types';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { User, GetCompanyResourcesDocument, CompanyResourceResourceTypeEnum } from '@/graphql/generated/graphql';
import { titleizeCamelCase } from '@/utils/helperFunctions';
import { AddressType } from '@/types/globals';

interface KickOffPdfViewProps {
  kickOffTitle: string;
  initiator: User;
  managers: ManagersType[];
  activities: Activity[];
  addresses: AddressType[];
  projectSettings: Settings[];
  noteSections: NoteSectionType[];
  setGeneratedPDF?: React.Dispatch<React.SetStateAction<Blob | null>>;
  isTemplateView?: boolean; // Optional prop to indicate if this is a template view
}

const KickOffPdfView: React.FC<KickOffPdfViewProps> = ({
  kickOffTitle,
  initiator,
  managers,
  activities,
  addresses,
  projectSettings,
  noteSections,
  setGeneratedPDF,
  isTemplateView = false,
}) => {
  const { currentUser } = useContext(CurrentUserContext);

  const { data } = useQuery(GetCompanyResourcesDocument, {
    skip: !currentUser,
  });

  const allEmployees = filter(
    data?.getCompanyResources || [],
    (resource) => resource.resourceType === CompanyResourceResourceTypeEnum.Human,
  );
  const defaultCurrency = find(projectSettings, { name: 'default-currency' })?.value || '€';
  const projectShifts = find(projectSettings, {
    name: 'project-shifts',
  })?.value
    ? JSON.parse(
        find(projectSettings, {
          name: 'project-shifts',
        })!.value!,
      )
    : [];

  // 2. Ref to grab the HTML content
  const documentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const generatePdf = async () => {
      if (!documentRef.current) return;

      try {
        // Wait for fonts to be ready to ensure icons/text render correctly in the screenshot
        await document.fonts.ready;

        // 3. Convert HTML to Canvas
        const canvas = await html2canvas(documentRef.current, {
          scale: 2, // Higher scale for better resolution (Retina support)
          useCORS: true, // Handle cross-origin images if any
          logging: false,
          backgroundColor: '#ffffff', // Ensure white background
        });

        // 4. Calculate dimensions
        // We set the PDF width to A4 width (210mm) and calculate height dynamically
        // so the design doesn't get cut off.
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = 210; // mm (A4 width)
        const componentWidth = documentRef.current.offsetWidth;
        const componentHeight = documentRef.current.offsetHeight;

        // Calculate PDF height based on the ratio of the original HTML
        const pdfHeight = (componentHeight * pdfWidth) / componentWidth;

        // 5. Create PDF
        // Orientation: portrait, unit: mm, format: custom [width, height]
        const pdf = new jsPDF('p', 'mm', [pdfWidth, pdfHeight]);

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        // 6. Output as Blob and Store in State
        const pdfBlob = pdf.output('blob');
        // const blobUrl = URL.createObjectURL(pdfBlob);

        if (setGeneratedPDF) {
          setGeneratedPDF(pdfBlob);
        }
      } catch (error) {
        console.error('Error generating PDF:', error);
      }
    };

    // Small timeout to ensure DOM is fully painted before capturing
    const timeoutId = setTimeout(() => {
      generatePdf();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [kickOffTitle, managers, activities, addresses, projectSettings, noteSections]);

  return (
    <Grid container spacing={2} sx={{ ml: 0 }} size={12}>
      <div className="kickoff-wrapper">
        <div className="kick-off-document" ref={documentRef}>
          {/* --- Header --- */}
          <header className="kick-off-header">
            <div className="kick-off-header-eyebrow">
              Project Kick-Off Document {isTemplateView ? '(Template)' : ''}
            </div>
            <h1>{kickOffTitle}</h1>
          </header>

          {/* --- Initiator --- */}
          {initiator && (
            <div className="kick-off-initiator">
              <div className="kick-off-user-icon"></div>
              <div className="kick-off-initiator-details">
                <span className="kick-off-label">Initiated by</span>
                <strong>
                  {initiator.firstName} {initiator.lastName}
                </strong>
                <span>{initiator.email}</span>
              </div>
            </div>
          )}

          {/* --- Project Management --- */}
          <div className="kick-off-section">
            <div className="kick-off-section-title">Project Management</div>
            <hr className="kick-off-divider" />
            <ul className="kick-off-pm-list">
              {map(managers, (manager, index) => {
                const employee = allEmployees.find(
                  (res) =>
                    res.id === manager.companyResourceId ||
                    res.id === manager.employmentId ||
                    (manager.userId && res.user?.id === manager.userId),
                );
                return (
                  <li key={index} className="kick-off-pm-item">
                    <span>
                      <strong>{titleizeCamelCase(employee ? employee.name : 'Unknown Employee')}</strong>
                      {manager.title ? ` (${manager.title})` : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* --- Scheduled Activities --- */}
          <div className="kick-off-section">
            <div className="kick-off-section-title">
              <div className="kick-off-icon-calendar"></div> Scheduled Activities
            </div>
            <hr className="kick-off-divider" />

            {map(activities, (activity, index) => (
              <div key={index} className={`kick-off-activity border-${['teal', 'gold', 'purple'][index % 3]}`}>
                <h3>{activity.title}</h3>
                <p className="kick-off-activity-description">{activity.description}</p>
                <div className="kick-off-date-row">
                  <div className="kick-off-icon-date-mini"></div>
                  {activity.startTime
                    ? new Date(activity.startTime).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'TBD'}
                </div>
              </div>
            ))}
          </div>

          {/* --- Project Addresses --- */}
          <div className="kick-off-section">
            <div className="kick-off-section-title">
              <div className="kick-off-icon-pin"></div> Project Addresses
            </div>
            <hr className="kick-off-divider" />
            <div className="kick-off-address-grid">
              {map(addresses, (address, index) => (
                <div key={index} className="kick-off-address-card">
                  <h4>
                    <span className="kick-off-icon-pin-mini"></span> {`Location ${index + 1}`}
                  </h4>
                  <p>{address.street}</p>
                  <p className="kick-off-address-sub">{address.city}</p>
                  <p className="kick-off-address-sub">United Kingdom - {address.zip}</p>
                </div>
              ))}
            </div>
          </div>

          {/* --- Project Configuration --- */}
          <div className="kick-off-section">
            <div className="kick-off-section-title">
              <div className="kick-off-icon-gear"></div> Project Configuration
            </div>
            <hr className="kick-off-divider" />

            <div className="kick-off-config-row">
              <span>Weekend Work</span>
              {find(projectSettings, { name: 'enable-weekends', value: 'true' }) ? (
                <span className="kick-off-badge-enabled">Enabled</span>
              ) : (
                <span className="kick-off-badge-disabled">Disabled</span>
              )}
            </div>
            <div className="kick-off-config-row">
              <span>Default Currency</span>
              <div className="kick-off-currency-box">{defaultCurrency}</div>
            </div>

            <div style={{ marginTop: '15px', fontSize: '12px', color: '#4b5563' }}>Work Shifts</div>
            <div className="kick-off-shift-table">
              {map(projectShifts, (shift: { name: string; startTime: string; endTime: string }, index: number) => (
                <div key={index} className="kick-off-shift-row">
                  <span className="kick-off-shift-name" style={{ paddingLeft: '10px' }}>
                    {shift.name}
                  </span>
                  <span className="kick-off-shift-time">
                    {shift.startTime} - {shift.endTime}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* --- Notes Section --- */}
          {map(noteSections || [], (noteSection, index) => (
            <div className="kick-off-section" key={index}>
              <div className="kick-off-section-title">
                <div className="kick-off-icon-check"></div> {titleize(noteSection.sectionName)}
              </div>
              <hr className="kick-off-divider" />
              <ul className="kick-off-custom-list">
                {map(noteSection.sectionNotes, (note, noteIndex) => (
                  <li key={noteIndex}>
                    <span className="kick-off-list-num">{noteIndex + 1}</span>
                    <div className="kick-off-list-content">{note}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* --- Footer --- */}
          <div className="kick-off-footer">
            <span>
              Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span>Confidential - Internal Use Only</span>
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default KickOffPdfView;
